import { useState } from 'react'
import { TaxInfo } from '../../../../types/mutual-fund-detail.types'
import { roundOffAndFormatNumberWithCurrency } from '../../../../utils/utils'
import { calculateTaxLiability } from '../../../../utils/mf-execution/taxLiabilityUtils'
import NameValueTable from '../../../ui-components/NameValueTable'
import NameValueTableRow from '../../../ui-components/NameValueTableRow'
import InPlaceNameValueAccordion from '../../../ui-components/InPlaceNameValueAccordion'
import BottomSheetContent from '../../BottomSheetContent'

export const TaxLiabilityBottomSheet = ({
  heading,
  onDismiss,
  withdrawalAmount,
  exitLoad,
  stcgInfo,
  ltcgInfo,
  grandfatheringRuleApplicable = false
}: {
  heading: string
  onDismiss: () => void
  withdrawalAmount: number
  exitLoad: number
  stcgInfo: TaxInfo
  ltcgInfo: TaxInfo
  grandfatheringRuleApplicable?: boolean
}) => {
  return (
    <BottomSheetContent
      heading={heading}
      onDismiss={onDismiss}
      bottomSheetHeadingClassName='!font-[500]'
      bottomSheetBodyClassName='!px-0 !pb-4'
    >
      <TaxLiabilityBottomSheetContent
        testLocator={'MFE_ENTER_AMOUNT_BS_TAX_LIABILITY'}
        withdrawalAmount={withdrawalAmount}
        exitLoad={exitLoad}
        stcgInfo={stcgInfo}
        ltcgInfo={ltcgInfo}
        grandfatheringRuleApplicable={grandfatheringRuleApplicable}
      />
    </BottomSheetContent>
  )
}

export const TaxLiabilityBottomSheetContent = ({
  testLocator,
  withdrawalAmount,
  exitLoad,
  stcgInfo,
  ltcgInfo,
  grandfatheringRuleApplicable
}: {
  testLocator: string
  withdrawalAmount: number
  exitLoad: number
  stcgInfo: TaxInfo
  ltcgInfo: TaxInfo
  grandfatheringRuleApplicable: boolean
}) => {
  const [isStcgExpanded, setIsStcgExpanded] = useState(false)
  const [isLtcgExpanded, setIsLtcgExpanded] = useState(false)

  const taxCalculation = calculateTaxLiability(withdrawalAmount, exitLoad, stcgInfo, ltcgInfo)
  return (
    <div test-locator={testLocator} className='flex flex-col px-5 border-t border-[#F0F4F7]'>
      <div className='flex flex-col pt-5 px-1'>
        <NameValueTable
          data={[
            {
              name: 'Withdrawal Amount',
              value: `${roundOffAndFormatNumberWithCurrency(withdrawalAmount)}`
            },
            {
              name: 'Exit Load',
              value: `${roundOffAndFormatNumberWithCurrency(exitLoad)}`
            },
            {
              name: 'Pre-Tax Value',
              value: `${roundOffAndFormatNumberWithCurrency(taxCalculation.preTaxValue)}`
            }
          ]}
          gap={12}
        />
        <div className='text-[#8E9393] text-[13px] font-[400]'>This gets credited to your bank</div>
        <div className='w-full pt-5 pb-4'>
          <div className='w-full h-[1px] bg-[#F0F4F7]' />
        </div>
      </div>

      <div className='flex flex-col w-full'>
        {stcgInfo.investedAmount > 0 ? (
          <STCGTaxSection
            stcgInfo={stcgInfo}
            stcgTax={taxCalculation.stcgTax}
            isExpanded={isStcgExpanded}
            onToggleExpanded={() => setIsStcgExpanded((prev) => !prev)}
          />
        ) : (
          <div className='w-full pb-4'>
            <NameValueTableRow
              name='STCG Tax'
              value={`${roundOffAndFormatNumberWithCurrency(taxCalculation.stcgTax)}`}
              nameClassName='!text-[#6A6E6E] !text-[13px] !font-[500]'
              valueClassName='!text-[#6A6E6E] !text-[13px] !font-[500]'
            />
          </div>
        )}

        {ltcgInfo.investedAmount > 0 ? (
          <LTCGTaxSection
            ltcgInfo={ltcgInfo}
            ltcgTax={taxCalculation.ltcgTax}
            isExpanded={isLtcgExpanded}
            onToggleExpanded={() => setIsLtcgExpanded((prev) => !prev)}
            grandfatheringRuleApplicable={grandfatheringRuleApplicable}
          />
        ) : (
          <div className='w-full pb-4'>
            <NameValueTableRow
              name='LTCG Tax'
              value={`${roundOffAndFormatNumberWithCurrency(taxCalculation.ltcgTax)}`}
              nameClassName='!text-[#6A6E6E] !text-[13px] !font-[500]'
              valueClassName='!text-[#6A6E6E] !text-[13px] !font-[500]'
            />
          </div>
        )}

        <div className='pb-6 w-full'>
          <NameValueTableRow
            name='Tax Liability'
            value={`${roundOffAndFormatNumberWithCurrency(taxCalculation.totalTaxLiability)}`}
            nameClassName='!text-[#232323] !font-[500]'
            valueClassName='!text-[22px]'
          />
        </div>
        <hr />
        <div className='w-full pt-4 pb-5'>
          <NameValueTableRow
            name='Post-Tax Value'
            value={`${roundOffAndFormatNumberWithCurrency(taxCalculation.postTaxValue)}`}
            nameClassName='!text-[#3D3D3D] !text-[13px]'
          />
        </div>
        <hr />
        <div className='text-[#6A6E6E] text-[13px] font-[400] pt-5 pb-8'>
          Above calculation is an estimate. Final values depend on the date your order is processed.
        </div>
      </div>
    </div>
  )
}

export const STCGTaxSection = ({
  stcgInfo,
  stcgTax,
  isExpanded,
  onToggleExpanded
}: {
  stcgInfo: TaxInfo
  stcgTax: number
  isExpanded: boolean
  onToggleExpanded: () => void
}) => {
  return (
    <div
      style={{
        width: isExpanded ? 'calc(100% + 24px)' : '100%',
        marginLeft: isExpanded ? '-12px' : '0px'
      }}
      className={`w-full ${isExpanded ? 'pb-3' : 'pb-4'}`}
    >
      <InPlaceNameValueAccordion
        name={`STCG Tax @ ${stcgInfo?.taxRate ?? 0}%`}
        value={`${roundOffAndFormatNumberWithCurrency(stcgTax)}`}
        nameClassName='!text-[#095EB2] !font-[500] !text-[13px]'
        valueClassName='!text-[#095EB2] !text-[13px]'
        chevronColor='#095EB2'
        accordionClassName={isExpanded ? '!bg-[#E6F2FF]' : ''}
        isExpanded={isExpanded}
        alwaysShowValue={true}
        onAccordionClick={onToggleExpanded}
      >
        <NameValueTable
          data={[
            {
              name: 'Investment held for less than 1Y',
              value: `${roundOffAndFormatNumberWithCurrency(stcgInfo.investedAmount)}`
            },
            {
              name: 'Short term capital gains',
              value: `${stcgInfo.gains < 0 ? '-' : ''}${roundOffAndFormatNumberWithCurrency(Math.abs(stcgInfo.gains))}`
            }
          ]}
          nameClassName='!text-[#3D3D3D] !text-[13px]'
          valueClassName='!text-[13px]'
          gap={4}
        />
      </InPlaceNameValueAccordion>
    </div>
  )
}

export const LTCGTaxSection = ({
  ltcgInfo,
  ltcgTax,
  isExpanded,
  onToggleExpanded,
  grandfatheringRuleApplicable
}: {
  ltcgInfo: TaxInfo
  ltcgTax: number
  isExpanded: boolean
  onToggleExpanded: () => void
  grandfatheringRuleApplicable: boolean
}) => {
  return (
    <div
      style={{
        width: isExpanded ? 'calc(100% + 24px)' : '100%',
        marginLeft: isExpanded ? '-12px' : '0px'
      }}
      className={`w-full ${isExpanded ? 'pb-3' : 'pb-4'}`}
    >
      <InPlaceNameValueAccordion
        name={`LTCG Tax @ ${ltcgInfo?.taxRate ?? 0}%`}
        value={`${roundOffAndFormatNumberWithCurrency(ltcgTax)}`}
        nameClassName='!text-[#095EB2] !font-[500] !text-[13px]'
        valueClassName='!text-[#095EB2] !text-[13px]'
        chevronColor='#095EB2'
        accordionClassName={isExpanded ? '!bg-[#E6F2FF]' : ''}
        isExpanded={isExpanded}
        alwaysShowValue={true}
        onAccordionClick={onToggleExpanded}
      >
        <div className='flex flex-col w-full'>
          <NameValueTable
            data={[
              {
                name: 'Investment held for 1Y or more',
                value: `${roundOffAndFormatNumberWithCurrency(ltcgInfo.investedAmount)}`
              },
              {
                name: 'Long term capital gains',
                value: `${ltcgInfo.gains < 0 ? '-' : ''}${roundOffAndFormatNumberWithCurrency(Math.abs(ltcgInfo.gains))}`
              }
            ]}
            nameClassName='!text-[#3D3D3D] !text-[13px]'
            valueClassName='!text-[13px]'
            gap={4}
          />
          {grandfatheringRuleApplicable && (
            <>
              <hr className='mt-2' />
              <div className='text-[#6A6E6E] font-[400] text-[13px] pt-4'>
                Above gains may be lower due to updated tax rules (Section 112A).
              </div>
            </>
          )}
        </div>
      </InPlaceNameValueAccordion>
    </div>
  )
}
